/**
 * BrokerPlatformDetails Component
 * Displays broker platform capabilities and metadata in the account details tab
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
} from "@mui/material";
import {
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Assignment as OrderIcon,
} from "@mui/icons-material";
import { Icon } from "../../../../components/ds";
import { services } from "../../../../services/api";
import LoadingSpinner from "../../Components/LoadingSpinner";
import { sessionStatusOf } from "../../accountStatus";

const BrokerPlatformDetails = ({ account, rateLimitUsage }) => {
  // One reading of the session for the whole feature — see ../../accountStatus.
  const session = sessionStatusOf(account);
  // Broker metadata state
  const [brokerMetadata, setBrokerMetadata] = useState(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  //! broker-capability-single-source (Part A) — order-type/product capability now comes from the
  //  class-keyed order_manifest (GET /brokers/{account}/capabilities via the gateway), NOT from the
  //  decommissioned broker_metadata.order_types / api_capabilities. markets/limits/supports_gtt
  //  below still read broker_metadata (no manifest equivalent). manifestClasses = the payload's
  //  `classes` map (classKey -> {products, order_types, varieties, tif, attributes}); null on any
  //  fetch failure/missing data -> the capability section simply doesn't render (graceful empty).
  const [manifestClasses, setManifestClasses] = useState(null);

  // Load broker metadata when account is loaded
  const loadBrokerMetadata = useCallback(async () => {
    if (!account?.broker_id) {
      return;
    }
    
    try {
      setIsLoadingMetadata(true);
      const response = await services.brokers.getBroker(account.broker_id);
      
      // Check if we have a successful HTTP response
      if (response.status === 200 && response.data) {
        // Extract broker metadata from the response
        const brokerData = response.data;
        const metadata = brokerData.broker_metadata;
        
        if (metadata) {
          setBrokerMetadata(metadata);
        }
      }
    } catch (err) {
      console.error("Error fetching broker metadata:", err);
    } finally {
      setIsLoadingMetadata(false);
    }
  }, [account]);

  useEffect(() => {
    if (account?.broker_id) {
      loadBrokerMetadata();
    }
  }, [account, loadBrokerMetadata]);

  //--> Fetch the class-keyed capability manifest for THIS account (api.js -> gateway -> BIS
  //    ownership/superuser relay -> adapter GET /capabilities, S2S — never the adapter host:port
  //    directly). Envelope-tolerant unwrap mirrors fetchCapabilitiesForAccounts /
  //    ModifyOrderModal (`res.data.data ?? res.data`). Fail-safe: any error / missing `classes`
  //    leaves manifestClasses null and the capability section is simply omitted (never crashes).
  const loadCapabilities = useCallback(async () => {
    if (!account?.account_id) {
      setManifestClasses(null);
      return;
    }
    try {
      const res = await services.brokerAccounts.getBrokerCapabilities(account.account_id);
      const payload = res?.data?.data ?? res?.data ?? null;
      const classes = payload?.classes;
      setManifestClasses(classes && typeof classes === "object" ? classes : null);
    } catch (err) {
      console.error("[DIAG] broker capability manifest fetch failed (graceful empty)", err);
      setManifestClasses(null);
    }
  }, [account]);

  useEffect(() => {
    if (account?.account_id) {
      loadCapabilities();
    }
  }, [account, loadCapabilities]);

  // MetadataSection component for displaying broker capabilities
  const MetadataSection = ({ title, data, type = "table", chipColor = "primary", icon }) => {
    if (!data) return null;

    // Special handling for rate limits
    if (title === "API Rate Limits") {
      return (
        <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] hover:shadow-md transition-shadow duration-200">
          <div className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-bg-3">
                {icon}
              </div>
              <Typography 
                variant="subtitle1" 
                className="font-semibold text-fg-1"
              >
                {title}
              </Typography>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
              {Object.entries(data).map(([period, limit]) => (
                <div 
                  key={period}
                  className="p-4 rounded-lg bg-bg-3/80 dark:bg-bg-3/50 border border-[var(--divider)] hover:bg-bg-3/80 dark:hover:bg-bg-3/70 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <Typography 
                        variant="body2" 
                        className="text-fg-2 font-medium mb-1"
                      >
                        {period.charAt(0).toUpperCase() + period.slice(1).replace(/_/g, " ")}
                      </Typography>
                      <Typography 
                        variant="h6" 
                        className="font-bold text-fg-1"
                      >
                        {limit}
                      </Typography>
                    </div>
                    <Typography 
                      variant="caption" 
                      className="text-fg-2 text-xs"
                    >
                      requests
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] hover:shadow-md transition-shadow duration-200">
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-bg-3">
              {icon}
            </div>
            <Typography 
              variant="subtitle1" 
              className="font-semibold text-fg-1"
            >
              {title}
            </Typography>
          </div>
          {type === "chips" ? (
            <div className="flex flex-wrap gap-2">
              {data.map((item, idx) => (
                <span
                  key={idx}
                  className={`ds-badge sm ${chipColor === "secondary" ? "role" : "neutral"}`}
                >
                  {item}
                </span>
              ))}
            </div>
          ) : (
            <div className="bg-bg-3/80 dark:bg-bg-3/50 rounded-lg p-3 border border-[var(--divider)]">
              <Typography 
                variant="body2" 
                className="text-fg-2 font-mono text-sm"
              >
                {Array.isArray(data) ? data.join(", ") : JSON.stringify(data, null, 2)}
              </Typography>
            </div>
          )}
        </div>
      </div>
    );
  };

  //--> broker-capability-single-source: SUPPORTED EXCHANGES derived from the class-keyed manifest
  //    (getBrokerCapabilities) class-keys ("EQUITY:NSE_BSE" -> NSE, BSE), replacing the decommissioned
  //    generic broker_metadata.markets seed (which over-listed CDS/MCX identically for every broker).
  const manifestExchanges = () => {
    const classes = manifestClasses;
    if (!classes || typeof classes !== "object") return null;
    const set = new Set();
    Object.keys(classes).forEach((classKey) => {
      const group = classKey.split(":")[1] || ""; // e.g. "NSE_BSE"
      group.split("_").forEach((ex) => { if (ex) set.add(ex); }); // NSE, BSE
    });
    return set.size ? Array.from(set).sort() : null;
  };

  //--> broker-capability-single-source (Part A): renders order-types + products PER instrument
  //    class from the class-keyed manifest (getBrokerCapabilities), replacing the decommissioned
  //    broker_metadata.order_types badges + api_capabilities panel. A canonical value is SUPPORTED
  //    iff its wire entry carries a non-null wire_code — the same present-with-wire-code semantics
  //    the fail-closed gate uses (instrumentClassCapabilityGate.js:_isAllowed). broker_label is the
  //    "you know this as ___" hint; definition is the ⓘ tooltip. Graceful empty: renders nothing
  //    when there is no manifest data (never crashes).
  const renderManifestCapabilities = () => {
    const classes = manifestClasses;
    if (!classes || typeof classes !== "object" || Object.keys(classes).length === 0) return null;

    //--> supported canonical values on an axis = entries whose wire_code is non-null.
    const supportedEntries = (block, axis) =>
      Object.entries(block?.[axis] || {}).filter(([, v]) => v && v.wire_code != null);

    const classKeys = Object.keys(classes).sort();

    return (
      <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] hover:shadow-md transition-shadow duration-200">
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-bg-3">
              <OrderIcon className="text-fg-2" />
            </div>
            <div>
              <Typography variant="subtitle1" className="font-semibold text-fg-1">
                Order Capabilities (via API)
              </Typography>
              <Typography variant="caption" className="text-fg-2">
                Supported order types and products per instrument class — from the live capability manifest
              </Typography>
            </div>
          </div>
          <div className="space-y-4">
            {classKeys.map((classKey) => {
              const block = classes[classKey];
              const orderTypes = supportedEntries(block, "order_types");
              const products = supportedEntries(block, "products");
              return (
                <div
                  key={classKey}
                  className="bg-bg-3/80 dark:bg-bg-3/50 rounded-lg p-4 border border-[var(--divider)]"
                >
                  <Typography variant="body2" className="font-semibold text-fg-1 mb-3">
                    {classKey.replace(":", " \u00b7 ")}
                  </Typography>

                  <Typography variant="caption" className="text-fg-2 block mb-1.5">
                    Order Types
                  </Typography>
                  {orderTypes.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {orderTypes.map(([canonical, v]) => (
                        <span key={canonical} className="ds-badge sm role" title={v.definition || ""}>
                          {canonical}
                          {v.broker_label ? ` (${v.broker_label})` : ""}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <Typography variant="caption" className="text-fg-3 block mb-3">
                      None
                    </Typography>
                  )}

                  <Typography variant="caption" className="text-fg-2 block mb-1.5">
                    Products
                  </Typography>
                  {products.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {products.map(([canonical, v]) => (
                        <span key={canonical} className="ds-badge sm neutral" title={v.definition || ""}>
                          {canonical}
                          {v.broker_label ? ` (${v.broker_label})` : ""}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <Typography variant="caption" className="text-fg-3">
                      None
                    </Typography>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Helper to render usage vs. limit in the same card style as API Rate Limits
  const renderUsageVsLimit = () => {
    if (!rateLimitUsage) return null;
    const { usage = {}, rate_limits = {} } = rateLimitUsage;
    const periods = [
      { key: 'per_second', label: 'Per Second' },
      { key: 'per_minute', label: 'Per Minute' },
      { key: 'per_day', label: 'Per Day' },
    ];
    return (
      <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] hover:shadow-md transition-shadow duration-200 mt-6">
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-bg-3">
              <SpeedIcon className="text-fg-2" />
            </div>
            <Typography variant="subtitle1" className="font-semibold text-fg-1">
              API Usage vs. Rate Limits
            </Typography>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {periods.map(({ key, label }) => {
              const limit = rate_limits[key];
              const value = usage[key];
              const percent = limit && value !== undefined && limit > 0 ? Math.round((value / limit) * 100) : 0;
              let barColor = 'bg-profit';
              let percentColor = 'text-[var(--badge-profit-fg)]';
              if (percent > 80) {
                barColor = 'bg-loss';
                percentColor = 'text-[var(--badge-loss-fg)]';
              } else if (percent > 60) {
                barColor = 'bg-warning';
                percentColor = 'text-[var(--badge-warn-fg)]';
              }
              return (
                <div key={key} className="p-4 rounded-lg bg-bg-3/80 dark:bg-bg-3/50 border border-[var(--divider)] hover:bg-bg-3/80 dark:hover:bg-bg-3/70 transition-colors duration-200 flex flex-col justify-between h-full">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Typography variant="body2" className="text-fg-2 font-medium mb-1">
                        {label}
                      </Typography>
                      <Typography variant="h6" className="font-bold text-fg-1">
                        {value !== undefined ? value : '--'} of {limit !== undefined ? limit : '--'}
                      </Typography>
                      <Typography variant="caption" className="text-fg-2 text-xs block">
                        {limit !== undefined ? 'requests' : 'No limits defined by broker'}
                      </Typography>
                    </div>
                  </div>
                  {limit !== undefined && value !== undefined && limit > 0 && (
                    <>
                      <div className="flex flex-col gap-1 mt-3">
                        <div className="flex items-center gap-3">
                          <div className="relative flex-1 h-2.5 bg-bg-3 dark:bg-bg-3 rounded-full overflow-hidden">
                            <div
                              className={`absolute left-0 top-0 h-full rounded-full transition-all duration-300 ${barColor}`}
                              style={{ width: `${Math.min(100, percent)}%` }}
                            />
                          </div>
                          <span className={`shrink-0 text-xs font-bold tabular-nums ${percentColor}`}>
                            {percent}%
                          </span>
                        </div>
                        <Typography variant="caption" className="text-fg-2 mt-2 block text-right">
                          Threshold: {limit} req/{label.toLowerCase().replace(' ', '')}
                        </Typography>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)]">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-accent/10">
            <Icon name="info" className="text-accent" />
          </div>
          <Typography
            variant="h6"
            className="text-fg-1 font-semibold"
          >
            Platform Capabilities
          </Typography>
        </div>

        {/* Loading state for metadata */}
        {isLoadingMetadata && (
          <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)]">
            <div className="flex justify-center items-center py-16">
              <LoadingSpinner size="small" />
              <Typography 
                variant="body2" 
                className="ml-3 text-fg-2"
              >
                Loading capabilities...
              </Typography>
            </div>
          </div>
        )}

        {/* Error state */}
        {!isLoadingMetadata && !brokerMetadata && (
          <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)]">
            <div className="text-center py-16">
              <div className="p-3 rounded-full bg-bg-3 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Icon name="error" size={24} className="text-fg-3" />
              </div>
              <Typography 
                variant="body1" 
                className="text-fg-2 font-medium mb-1"
              >
                Capabilities unavailable
              </Typography>
              <Typography 
                variant="body2" 
                className="text-fg-3"
              >
                Unable to fetch broker platform details
              </Typography>
            </div>
          </div>
        )}

        {/* Broker metadata display */}
        {!isLoadingMetadata && brokerMetadata && (
          <div className="space-y-5">
            {/* Supported Exchanges — from the class-keyed manifest (getBrokerCapabilities), NOT the
                decommissioned generic broker_metadata.markets seed (which listed CDS/MCX for every
                broker uniformly). ICICI resolves to NSE/BSE/NFO/BFO (no MCX/CDS). */}
            {manifestExchanges() && (
              <MetadataSection
                title="Supported Exchanges"
                data={manifestExchanges()}
                type="chips"
                chipColor="primary"
                icon={<Icon name="gain" className="text-fg-2" />}
              />
            )}

            {/* Order-type + product capabilities — broker-capability-single-source (Part A):
                sourced from the class-keyed manifest (getBrokerCapabilities), replacing the
                decommissioned broker_metadata.order_types badges + api_capabilities panel.
                markets / limits / supports_gtt below still read broker_metadata. */}
            {renderManifestCapabilities()}

            {/* API Rate Limits */}
            {brokerMetadata.limits && (
              <MetadataSection
                title="API Rate Limits"
                data={brokerMetadata.limits}
                type="rate_limits"
                icon={<SpeedIcon className="text-fg-2" />}
              />
            )}

            {/* API Usage vs. Rate Limits */}
            {renderUsageVsLimit()}

            {/* Platform Features & Connection Status Combined */}
            {((brokerMetadata.supports_gtt !== undefined || brokerMetadata.features) || account.status || account.session_status) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Platform Features */}
                {(brokerMetadata.supports_gtt !== undefined || brokerMetadata.features) && (
                  <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] hover:shadow-md transition-shadow duration-200">
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-lg bg-bg-3">
                          <SecurityIcon className="text-fg-2" />
                        </div>
                        <Typography 
                          variant="subtitle1" 
                          className="font-semibold text-fg-1"
                        >
                          Features
                        </Typography>
                      </div>
                      
                      <div className="space-y-3">
                        {brokerMetadata.supports_gtt !== undefined && (
                          <div className="flex items-center justify-between p-3 rounded-lg bg-bg-3/80 dark:bg-bg-3/50 border border-[var(--divider)]">
                            <Typography 
                              variant="body2" 
                              className="text-fg-2 font-medium"
                            >
                              GTT Orders
                            </Typography>
                            <span
                              className={`ds-badge sm ${brokerMetadata.supports_gtt ? "profit" : "neutral"}`}
                            >
                              {brokerMetadata.supports_gtt ? <Icon name="success" size={13} /> : <Icon name="error" size={13} />}
                              {brokerMetadata.supports_gtt ? "Supported" : "Not Available"}
                            </span>
                          </div>
                        )}
                        
                        {brokerMetadata.features && Array.isArray(brokerMetadata.features) && (
                          <div className="space-y-2">
                            <Typography 
                              variant="body2" 
                              className="text-fg-2 font-medium"
                            >
                              Additional Features
                            </Typography>
                            <div className="flex flex-wrap gap-2">
                              {brokerMetadata.features.map((feature, idx) => (
                                <span key={idx} className="ds-badge sm neutral">
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Connection Status */}
                <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] hover:shadow-md transition-shadow duration-200">
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-bg-3">
                        <Icon name="success" className="text-fg-2" />
                      </div>
                      <Typography 
                        variant="subtitle1" 
                        className="font-semibold text-fg-1"
                      >
                        Status
                      </Typography>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-bg-3/80 dark:bg-bg-3/50 border border-[var(--divider)]">
                        <Typography 
                          variant="body2" 
                          className="text-fg-2 font-medium"
                        >
                          Account
                        </Typography>
                        <span
                          className={`ds-badge sm ${account.status === "verified" ? "profit" : "neutral"}`}
                        >
                          {account.status === "verified" ? <Icon name="success" size={13} /> : <Icon name="error" size={13} />}
                          {account.status || "Unknown"}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 rounded-lg bg-bg-3/80 dark:bg-bg-3/50 border border-[var(--divider)]">
                        <Typography 
                          variant="body2" 
                          className="text-fg-2 font-medium"
                        >
                          Session
                        </Typography>
                        {/* Derived from the expiry, not account.session_status — that
                            field reads "active" whenever a session ROW exists, even an
                            expired one (services/broker_account.py), so this badge used
                            to say "active" beside a card showing "Expired". It also
                            printed the raw lowercase API string. See ../../accountStatus. */}
                        <span className={`ds-badge sm ${session.variant}`}>
                          {session.isActive ? <Icon name="success" size={13} /> : <Icon name="error" size={13} />}
                          {session.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


      </div>
    </div>
  );
};

export default BrokerPlatformDetails;
